import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import '../../styles/StripePricingTable.css';
import { auth, db } from '../../config/firebase';
import { doc, getDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { verifyAndUpdateSubscription } from '../../utils/subscriptionChecker';

// Stripe Price IDs
const MONTHLY_PRICE_ID = 'price_1SlyyWFhXQRpUSdZb8uUey7d'; 
const YEARLY_PRICE_ID = 'price_1Slz15FhXQRpUSdZp5pdu3GE'; 

const StripePricingTable = ({ userId }) => {
    const [loading, setLoading] = useState(false);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);

    useEffect(() => {
        if (!userId) return;

        // Check if user just returned from successful payment
        const checkPendingSubscription = async () => {
            const pending = localStorage.getItem('pendingSubscription');
            if (pending) {
                const data = JSON.parse(pending);
                // If less than 5 minutes old and matches current user
                if (data.userId === userId && (Date.now() - data.timestamp) < 300000) {
                    // Manually activate subscription in Firebase
                    try {
                        const userRef = doc(db, 'UserInformation', userId);
                        const userSnap = await getDoc(userRef);
                        
                        if (userSnap.exists()) {
                            const userData = userSnap.data();
                            
                            // Update user
                            await updateDoc(userRef, {
                                member: true,
                                subscriptionPlan: data.planType,
                                subscriptionStatus: 'active',
                                subscriptionActivatedAt: new Date(),
                                subscriptionCanceledAt: null
                            });
                            
                            // Update partner if exists
                            const partnerId = userData.partnerId || userData.inRelationWith;
                            if (partnerId) {
                                const partnerRef = doc(db, 'UserInformation', partnerId);
                                await updateDoc(partnerRef, {
                                    member: true,
                                    subscriptionPlan: data.planType,
                                    subscriptionStatus: 'active',
                                    subscriptionCanceledAt: null
                                });
                            }
                        }
                    } catch (error) {
                        console.error('Error activating subscription:', error);
                    }
                }
                localStorage.removeItem('pendingSubscription');
            }
        };
        
        checkPendingSubscription();

        // Verify subscription status on mount
        verifyAndUpdateSubscription(userId);

        // Listen to user's subscription status in real-time
        const userRef = doc(db, 'UserInformation', userId);
        const unsubscribe = onSnapshot(userRef, (snapshot) => {
            if (snapshot.exists()) {
                const userData = snapshot.data();
                setSubscriptionStatus({
                    member: userData.member || false,
                    subscriptionPlan: userData.subscriptionPlan
                });
                
                // Set current plan to hide the card for active subscription
                if (userData.member && userData.subscriptionPlan) {
                    setCurrentPlan(userData.subscriptionPlan);
                } else {
                    setCurrentPlan(null);
                }
            }
        });

        return () => unsubscribe();
    }, [userId]);

    const handleSubscribe = async (priceId, planType) => {
        if (!userId || !auth.currentUser) {
            alert("Please log in to subscribe.");
            return;
        }
        
        localStorage.setItem('pendingSubscription', JSON.stringify({
            userId: userId,
            email: auth.currentUser.email,
            planType: planType,
            timestamp: Date.now()
        }));
        
        const paymentLinks = {
            monthly: 'https://buy.stripe.com/test_3cI7sKgPQ0Sg7EBd2fgIo01',
            yearly: 'https://buy.stripe.com/test_cNibJ01UWfNagb77HVgIo00'
        };
        
        const link = paymentLinks[planType];
        const url = new URL(link);
        url.searchParams.append('prefilled_email', auth.currentUser.email);
        url.searchParams.append('client_reference_id', userId);
        
        // Open in new tab
        window.open(url.toString(), '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="stripe-pricing-container">
            
            {/* Free Plan */}
            <div className="pricing-card free">
                <div className="pricing-header free">Free Plan</div>
                <div className="pricing-content">
                    <div className="pricing-price">$0 <span className="pricing-period">/ forever</span></div>
                    <ul className="pricing-list">
                        <li>Basic Features</li>
                        <li>Limited Access</li>
                        <li>Community Support</li>
                    </ul>
                    <div className="pricing-button-container">
                        <Button 
                            label="Current Plan" 
                            disabled
                            className="pricing-button free"
                        />
                    </div>
                </div>
            </div>

            {/* Monthly Plan */}
            {currentPlan !== 'monthly' && (
            <div className="pricing-card">
                <div className="pricing-header">Monthly Plan</div>
                <div className="pricing-content">
                    <div className="pricing-price">$4.99 <span className="pricing-period">/ month</span></div>
                    <ul className="pricing-list">
                        <li>Full Access to All Features</li>
                        <li>Sync with Partner</li>
                        <li>Cancel Anytime</li>
                    </ul>
                    <div className="pricing-button-container">
                        <Button 
                            label="Subscribe Monthly" 
                            onClick={() => handleSubscribe(MONTHLY_PRICE_ID, 'monthly')}
                            className="pricing-button"
                        />
                    </div>
                </div>
            </div>
            )}

            {/* Yearly Plan */}
            {currentPlan !== 'yearly' && (
            <div className="pricing-card">
                <div className="pricing-header yearly">Yearly Plan</div>
                <div className="pricing-content">
                    <div className="pricing-price">$49.99 <span className="pricing-period">/ year</span></div>
                    <div className="pricing-save-badge">Save 17%</div>
                    <ul className="pricing-list">
                        <li>All Monthly Features</li>
                        <li>Best Value</li>
                        <li>Priority Support</li>
                    </ul>
                    <div className="pricing-button-container">
                        <Button 
                            label="Subscribe Yearly" 
                            onClick={() => handleSubscribe(YEARLY_PRICE_ID, 'yearly')}
                            className="pricing-button yearly"
                        />
                    </div>
                </div>
            </div>
            )}

        </div>
    );
};

export default StripePricingTable;
